import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Toolbar } from "@/components/ui/toolbar/Toolbar";
import { Button } from "@/components/ui/button/Button";
import TimePeriodSelector, {
  type Period,
} from "@/components/TimePeriodSelector";
import {
  downloadDelimited,
  type ExportFormat,
} from "../utils/downloadCSV";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGrid2Plus } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { LoadingIndicator } from "@/components/chat";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import type { ExtendedAssetInfo } from "../hooks/useAssetManager";
import type { ChartSettings } from "../hooks/usePlaygroundSettings";
import { estimatePeriodDateRange } from "../periodConfig";
import { StatisticsAPI } from "@/services/api";
import {
  STATISTIC_FIELD_LABELS,
  RELATIVE_STATISTICS,
  TRAILING_PERIOD_LABELS,
  type StatisticField,
  type TrailingPeriod,
  type StatsViewColumn,
  type StatisticColumn,
  type PeriodReturnColumn,
  type StatisticsTableResponse,
  type ColumnValue,
} from "@/services/api/statistics/types";

interface StatisticsTableAreaProps {
  selectedAssets: string[];
  assetDisplayNames: Record<string, string>;
  assetMetadata: Record<string, ExtendedAssetInfo>;
  chartSettings: Partial<ChartSettings>;
  periods: Period[];
  isLoading: boolean;
  isInitialized?: boolean;
  onPeriodChange: (period: string) => void;
  onCustomDateChange: (range: [string, string]) => void;
  onAddToDashboard?: () => void;
  columns: StatsViewColumn[];
  showBenchmarks?: boolean;
  /** Annual risk-free rate for Sharpe/Sortino calculations (e.g., 0.02 for 2%) */
  riskFreeRate?: number;
  /** Callback when risk-free rate changes */
  onRiskFreeRateChange?: (rate: number) => void;
  extendIncompleteWithProxy?: boolean;
}

function formatDisplayDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatStatValue(
  field: StatisticField | string,
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined) {
    return "—";
  }

  if (typeof value === "string") {
    return value;
  }

  if (isNaN(value)) {
    return "—";
  }

  if (field.startsWith("period_")) {
    return `${(value * 100).toFixed(2)}%`;
  }

  const percentageFields: StatisticField[] = [
    "cumulative_performance",
    "annualized_performance",
    "volatility",
    "upside_volatility",
    "downside_volatility",
    "maximum_drawdown",
    "percent_positive_periods",
    "percent_negative_periods",
    "best_period",
    "worst_period",
    "average_positive_periods",
    "average_negative_periods",
    "tracking_error",
    "relative_cumulative_performance",
    "relative_annualized_performance",
    "relative_volatility",
    "historical_var",
    "historical_cvar",
  ];

  const ratioFields: StatisticField[] = [
    "sharpe_ratio",
    "sortino_ratio",
    "calmar_ratio",
    "return_to_drawdown_ratio",
    "omega_ratio",
    "gain_loss_ratio",
    "profit_factor",
    "beta",
    "correlation",
    "information_ratio",
    "skewness",
    "kurtosis",
    "ulcer_index",
    "pain_index",
  ];

  const integerFields: StatisticField[] = [
    "num_positive_periods",
    "num_negative_periods",
    "peak_to_trough_days",
    "time_to_recovery",
    "average_drawdown_duration",
    "longest_winning_streak",
    "longest_losing_streak",
  ];

  if (percentageFields.includes(field as StatisticField)) {
    return `${(value * 100).toFixed(2)}%`;
  }

  if (ratioFields.includes(field as StatisticField)) {
    return value.toFixed(2);
  }

  if (integerFields.includes(field as StatisticField)) {
    return Math.round(value).toString();
  }

  return value.toFixed(2);
}

function getCellColorClass(
  field: StatisticField | string,
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || typeof value === "string") {
    return "";
  }

  if (field.startsWith("period_")) {
    if (value > 0) return "text-emerald-400";
    if (value < 0) return "text-rose-400";
    return "";
  }

  const positiveGoodFields: StatisticField[] = [
    "cumulative_performance",
    "annualized_performance",
    "sharpe_ratio",
    "sortino_ratio",
    "calmar_ratio",
    "return_to_drawdown_ratio",
    "omega_ratio",
    "gain_loss_ratio",
    "profit_factor",
    "correlation",
    "information_ratio",
    "percent_positive_periods",
    "relative_cumulative_performance",
    "relative_annualized_performance",
  ];

  const negativeGoodFields: StatisticField[] = [
    "maximum_drawdown",
    "volatility",
    "downside_volatility",
    "tracking_error",
    "ulcer_index",
    "pain_index",
    "historical_var",
    "historical_cvar",
  ];

  if (positiveGoodFields.includes(field as StatisticField)) {
    if (value > 0) return "text-emerald-400";
    if (value < 0) return "text-rose-400";
    return "";
  }

  if (negativeGoodFields.includes(field as StatisticField)) {
    if (value < -0.1) return "text-rose-400";
    if (value < 0) return "text-amber-400";
    return "text-emerald-400";
  }

  return "";
}

export const StatisticsTableArea: React.FC<StatisticsTableAreaProps> = ({
  selectedAssets,
  assetDisplayNames,
  assetMetadata,
  chartSettings,
  periods,
  isLoading: externalLoading,
  isInitialized = false,
  onPeriodChange,
  onCustomDateChange,
  onAddToDashboard,
  columns,
  showBenchmarks = false,
  riskFreeRate = 0.02,
  onRiskFreeRateChange,
  extendIncompleteWithProxy = false,
}) => {
  const [tableData, setTableData] = useState<StatisticsTableResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateRange = useMemo(
    () =>
      estimatePeriodDateRange(
        chartSettings.period,
        chartSettings.customDateRange,
      ),
    [chartSettings.period, chartSettings.customDateRange],
  );

  const isMaxPeriod = chartSettings.period === "max" || chartSettings.period === "all";

  // Filter columns and track their original indices for correct value lookup
  const { filteredColumns, originalIndices } = useMemo(() => {
    if (isMaxPeriod && !tableData?.start_date) {
      // Before we have resolved dates, show all columns
      return {
        filteredColumns: columns,
        originalIndices: columns.map((_, i) => i),
      };
    }

    const effectiveStartDate = isMaxPeriod && tableData?.start_date 
      ? tableData.start_date 
      : dateRange.start_date;

    const startDate = new Date(effectiveStartDate);
    const endDate = new Date(dateRange.end_date);

    const filtered: StatsViewColumn[] = [];
    const indices: number[] = [];

    columns.forEach((col, originalIndex) => {
      let shouldInclude = true;

      if (col.type === "period_return") {
        const periodId = (col as PeriodReturnColumn).period;

        if (periodId.startsWith("cy_")) {
          // Include calendar years that overlap with the date range (partial years allowed)
          const year = parseInt(periodId.substring(3), 10);
          const yearStart = new Date(year, 0, 1);
          const yearEnd = new Date(year, 11, 31);
          // Include if the year overlaps with our date range
          shouldInclude = yearStart <= endDate && yearEnd >= startDate;
        } else if (/^q\d_\d{4}$/.test(periodId)) {
          // Include quarters that overlap with the date range (partial quarters allowed)
          const year = parseInt(periodId.substring(3), 10);
          const quarter = parseInt(periodId.substring(1, 2), 10);
          const quarterStart = new Date(year, (quarter - 1) * 3, 1);
          const quarterEnd = new Date(year, quarter * 3, 0);
          // Include if the quarter overlaps with our date range
          shouldInclude = quarterStart <= endDate && quarterEnd >= startDate;
        } else if (/^m\d{1,2}_\d{4}$/.test(periodId)) {
          // Include months that overlap with the date range (partial months allowed)
          const match = periodId.match(/^m(\d{1,2})_(\d{4})$/);
          if (match) {
            const month = parseInt(match[1], 10) - 1;
            const year = parseInt(match[2], 10);
            const monthStart = new Date(year, month, 1);
            const monthEnd = new Date(year, month + 1, 0);
            // Include if the month overlaps with our date range
            shouldInclude = monthStart <= endDate && monthEnd >= startDate;
          }
        } else if (["ytd", "mtd", "qtd"].includes(periodId)) {
          shouldInclude = true;
        }
      }

      if (shouldInclude) {
        filtered.push(col);
        indices.push(originalIndex);
      }
    });

    return { filteredColumns: filtered, originalIndices: indices };
  }, [columns, dateRange.start_date, dateRange.end_date, isMaxPeriod, tableData?.start_date]);

  const resources = useMemo(
    () =>
      selectedAssets.map((assetId) => {
        const meta = assetMetadata[assetId];
        return {
          resource_type: (meta?.resourceType || "product") as
            | "product"
            | "portfolio"
            | "benchmark"
            | "index",
          resource_id: meta?.resourceId || assetId,
          stream_type: "default",
        };
      }),
    [selectedAssets, assetMetadata]
  );

  const fetchTableData = useCallback(async () => {
    if (selectedAssets.length === 0 || columns.length === 0) {
      setTableData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const timeRangePreset =
        chartSettings.period === "max"
          ? "MAX_COMMON"
          : chartSettings.period === "all"
            ? "MAX"
            : undefined;

      const response = await StatisticsAPI.calculateTable({
        resources,
        columns,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        frequency: (chartSettings.frequency as "daily" | "weekly" | "monthly") || "daily",
        time_range_preset: timeRangePreset,
        risk_free_rate: riskFreeRate,
        extend_incomplete_with_proxy: extendIncompleteWithProxy,
      });

      setTableData(response);
    } catch (err) {
      console.error("Error fetching table data:", err);
      setError(
        err instanceof Error ? err.message : "Failed to calculate statistics"
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedAssets,
    resources,
    columns,
    dateRange,
    chartSettings.period,
    chartSettings.frequency,
    riskFreeRate,
    extendIncompleteWithProxy,
  ]);

  useEffect(() => {
    if (isInitialized) {
      fetchTableData();
    }
  }, [fetchTableData, isInitialized]);

  const getColumnValue = useCallback(
    (resourceId: string, columnIndex: number): ColumnValue | null => {
      if (!tableData) return null;

      const resourceRow = tableData.resources.find(
        (r) => r.resource_id === resourceId
      );
      if (!resourceRow) return null;

      return resourceRow.values[columnIndex] || null;
    },
    [tableData]
  );

  const getColumnLabel = useCallback((col: StatsViewColumn): string => {
    if (col.type === "period_return") {
      const periodId = (col as PeriodReturnColumn).period;
      if (periodId.startsWith("cy_")) {
        return periodId.substring(3);
      }
      if (periodId.startsWith("q")) {
        const match = periodId.match(/^q(\d)_(\d{4})$/);
        if (match) {
          return `Q${match[1]} ${match[2]}`;
        }
      }
      if (periodId.startsWith("m")) {
        const match = periodId.match(/^m(\d{1,2})_(\d{4})$/);
        if (match) {
          const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          return `${monthNames[parseInt(match[1], 10) - 1]} ${match[2]}`;
        }
      }
      return periodId.toUpperCase();
    } else {
      const statCol = col as StatisticColumn;
      return STATISTIC_FIELD_LABELS[statCol.field] || statCol.field;
    }
  }, []);

  const handleExport = useCallback(
    (format: ExportFormat = "csv") => {
      if (!tableData || tableData.resources.length === 0) return;

      const cols = [
        { id: "resource", label: "Resource" },
        ...filteredColumns.map((col, idx) => ({
          id: `col_${idx}`,
          label: getColumnLabel(col),
        })),
      ];

      const rows = tableData.resources.map((resourceRow) => {
        const row: Record<string, unknown> = {
          resource: assetDisplayNames[resourceRow.resource_id] || resourceRow.resource_id,
        };

        filteredColumns.forEach((col, idx) => {
          // Use original index to get correct value from API response
          const originalIndex = originalIndices[idx];
          const colValue = resourceRow.values[originalIndex];
          const field = col.type === "period_return" 
            ? `period_${(col as PeriodReturnColumn).period}` 
            : (col as StatisticColumn).field;
          row[`col_${idx}`] = formatStatValue(field, colValue?.value);
        });

        return row;
      });

      const extension = format === "csv" ? "csv" : "tsv";
      downloadDelimited(
        rows,
        cols,
        `statistics_${tableData.start_date}_to_${tableData.end_date}.${extension}`,
        {
          mode: "statistics",
          format,
        }
      );
    },
    [tableData, filteredColumns, originalIndices, assetDisplayNames, getColumnLabel]
  );

  const showLoading = !isInitialized || isLoading || externalLoading;
  const hasColumns = filteredColumns.length > 0;
  const hasData = tableData && tableData.resources.length > 0;

  const displayStartDate = tableData?.start_date || dateRange.start_date;
  const displayEndDate = tableData?.end_date || dateRange.end_date;

  return (
    <div className="w-full rounded-lg flex flex-col h-full space-y-4">
      <Toolbar>
        <div className="flex items-center gap-3 ">
          <TimePeriodSelector
            period={chartSettings.period || "3y"}
            setPeriod={onPeriodChange}
            periods={periods}
            customDateRange={chartSettings.customDateRange || ["", ""]}
            setCustomDateRange={onCustomDateChange}
          />{" "}
          <div className="flex items-center flex-none gap-1.5 px-2.5 py-1 rounded-lg  ">
            <span className="text-sm text-slate-300 font-mono">
              {formatDisplayDate(displayStartDate)}
            </span>
            <span className="text-sm text-slate-500">→</span>
            <span className="text-sm text-slate-300 font-mono">
              {formatDisplayDate(displayEndDate)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            color="color"
            className="px-2 py-1 min-h-0 border-none shadow-none"
            onClick={() => handleExport("csv")}
            title="Export statistics data"
            disabled={!hasData}
            dropdown={true}
            dropdownItems={[
              {
                name: "Export CSV",
                onClick: () => handleExport("csv"),
              },
              {
                name: "Export TSV",
                onClick: () => handleExport("tsv"),
              },
            ]}
          >
            Export
          </Button>
          {onAddToDashboard && (
            <Button
              color="color"
              className="px-2 py-1 min-h-0 border-none shadow-none"
              onClick={onAddToDashboard}
              title="Add to dashboard"
            >
              <FontAwesomeIcon icon={faGrid2Plus} className="h-4 w-4" />
            </Button>
          )}
        </div>
      </Toolbar>

      <div className="flex-grow h-full relative overflow-auto">
        {showLoading ? (
          <div className="w-full h-full flex items-center justify-center rounded-lg">
            <div className="text-center p-6 space-y-12">
              <div className="flex items-center justify-center">
                <LoadingIndicator />
              </div>
              <p className="text-gray-500 dark:text-gray-400 font-mono">
                Calculating statistics
              </p>
            </div>
          </div>
        ) : selectedAssets.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center p-6">
              <div className="text-lg font-medium mb-2 dark:text-white">
                No resources selected
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                Use the search above to add resources, or create a new list to
                get started.
              </p>
            </div>
          </div>
        ) : !hasColumns ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center p-6">
              <div className="text-lg font-medium mb-2 dark:text-white">
                No statistics or periods selected
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                Select statistics fields or period returns from the settings
                panel.
              </p>
            </div>
          </div>
        ) : error ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center p-6">
              <div className="text-lg font-medium mb-2 text-rose-400">
                Error
              </div>
              <p className="text-gray-500 dark:text-gray-400">{error}</p>
              <Button color="indigo" className="mt-4" onClick={fetchTableData}>
                Retry
              </Button>
            </div>
          </div>
        ) : !hasData ? (
          <div className="w-full h-full flex items-center justify-center rounded-lg">
            <div className="text-center p-6">
              <div className="text-lg font-medium mb-2 dark:text-white">
                No Data Available
              </div>
              <p className="text-gray-500 dark:text-gray-400">
                Statistics could not be calculated for the selected resources
                and date range.
              </p>
            </div>
          </div>
        ) : (
          <Table dense striped className="w-full">
            <TableHead>
              <TableRow>
                <TableHeader className="sticky left-0 z-10 bg-[#2c3b50] min-w-[280px]">
                  Resource
                </TableHeader>
                {filteredColumns.map((col, colIndex) => {
                  if (col.type === "period_return") {
                    const periodId = (col as PeriodReturnColumn).period;
                    return (
                      <TableHeader
                        key={`period_${periodId}_${colIndex}`}
                        className="text-right min-w-[100px]"
                      >
                        <div className="flex flex-col">
                          <span>{getColumnLabel(col)}</span>
                          <span className="text-[10px] text-slate-400 font-normal">
                            Return
                          </span>
                        </div>
                      </TableHeader>
                    );
                  } else {
                    const statCol = col as StatisticColumn;
                    const isRelative = RELATIVE_STATISTICS.includes(statCol.field);
                    return (
                      <TableHeader key={`stat_${colIndex}`} className="text-right min-w-[120px]">
                        <div className="flex flex-col items-end">
                          <span>{getColumnLabel(col)}</span>
                          {statCol.lookback && (
                            <span className="text-[10px] font-normal">
                              ({TRAILING_PERIOD_LABELS[statCol.lookback as TrailingPeriod] || statCol.lookback})
                            </span>
                          )}
                          {statCol.frequency && !statCol.lookback && (
                            <span className="text-[10px] text-slate-400 font-normal capitalize">
                              {statCol.frequency}
                            </span>
                          )}
                          {isRelative && (
                            <span className="text-[10px] text-indigo-400 font-normal">
                              vs benchmark
                            </span>
                          )}
                        </div>
                      </TableHeader>
                    );
                  }
                })}
              </TableRow>
            </TableHead>
            <TableBody className="top-[1px] relative">
              {tableData.resources.map((resourceRow, rowIndex) => {
                const displayName = assetDisplayNames[resourceRow.resource_id] || resourceRow.resource_id;
                const proxyExtension = resourceRow.proxy_extension;
                const proxyTooltip = proxyExtension
                  ? `Includes proxy-extended performance${
                      proxyExtension.proxy_benchmark_name
                        ? ` (${proxyExtension.proxy_benchmark_name})`
                        : ""
                    }`
                  : displayName;

                return (
                  <TableRow key={resourceRow.resource_id}>
                    <TableCell
                      className="sticky left-0 z-10 font-medium"
                      style={`background-color: ${rowIndex % 2 === 0 ? "#2c3b50" : "#303f53"}`}
                    >
                      <div
                        className={`truncate max-w-[280px] ${
                          proxyExtension ? "underline decoration-dashed decoration-slate-400 underline-offset-4" : ""
                        }`}
                        title={proxyTooltip}
                      >
                        {displayName}
                      </div>
                    </TableCell>
                    {filteredColumns.map((col, colIndex) => {
                      // Use original index to get correct value from API response
                      const originalIndex = originalIndices[colIndex];
                      const colValue = resourceRow.values[originalIndex];
                      const value = colValue?.value ?? null;
                      
                      const field = col.type === "period_return"
                        ? `period_${(col as PeriodReturnColumn).period}`
                        : (col as StatisticColumn).field;
                      
                      const formatted = formatStatValue(field, value);
                      const colorClass = getCellColorClass(field, value);

                      if (colValue?.error) {
                        return (
                          <TableCell
                            key={`cell_${colIndex}`}
                            className="text-right tabular-nums text-slate-500"
                            title={colValue.error}
                          >
                            —
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell
                          key={`cell_${colIndex}`}
                          className={`text-right tabular-nums ${colorClass}`}
                        >
                          {formatted}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default StatisticsTableArea;
